/**
 * Locale configuration. Every page lives under `app/[lang]/`; paths without a
 * locale prefix are redirected by `proxy.ts`. Add new languages here (plus
 * their translations) — routing, static params and the redirect pick them up.
 */
/**
 * `de` is here for the legal documents and the journal (`/learn`) only — see
 * LOCALE_SCOPE below.
 *
 * `app/[lang]/layout.tsx` builds its static params from this list and 404s
 * any other locale via `isLocale` — there is no narrower way to serve German
 * legal pages without a second root layout. The cost is that every marketing
 * page also prerenders at `/de/*` in English;
 * `proxy.ts` redirects those to `/en/*` so none of them is reachable, and no
 * sitemap or hreflang advertises them.
 *
 * When the marketing pages are actually translated, drop `de` from
 * LOCALE_SCOPE — the proxy redirect and the sitemap both follow it.
 */
export const LOCALES = ['en', 'de'] as const;

export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

export const isLocale = (value: string): value is Locale =>
  (LOCALES as readonly string[]).includes(value);

/**
 * Locales that serve only part of the site, as path prefixes (after the
 * locale segment, e.g. `/learn` covers `/learn` and `/learn/*`). A locale
 * without an entry serves every page. `proxy.ts` redirects everything outside
 * the scope to DEFAULT_LOCALE, and the sitemap lists only in-scope pages — so
 * translating the German marketing pages means deleting (or widening) the `de`
 * entry here, and both pick it up with no further work.
 *
 * Paths are the canonical (default-locale) route paths: localized slugs such
 * as `/rechtliches/impressum` are rewritten onto `/legal/imprint` first.
 */
export const LOCALE_SCOPE: Partial<Record<Locale, readonly string[]>> = {
  de: ['/legal', '/learn'],
};

/** Whether `locale` serves the canonical route path `path` (see LOCALE_SCOPE). */
export const localeServes = (locale: Locale, path: string): boolean => {
  const scope = LOCALE_SCOPE[locale];
  return !scope || scope.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
};
